import { useEffect, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { Box, Button, ButtonGroup, Heading, Modal, Paragraph } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import { TourAvailabilityWizard } from '@zg-rentals/touring';
import { ZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing_Path } from '@zg-rentals/ts-z-calendar-client';
import { useZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing } from '@zg-rentals/ts-z-calendar-client-react-query';

import { useScreenSize } from '../../../hooks';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import accessUtils from '../../../utils/accessUtils';
import propertyUtils from '../../../utils/propertyUtils';

import { RENT_HOME_PEOPLE_ILLUSTRATION_URL } from '../../../constants/DigitalLibraryConstants';
import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';
import { getTourCalendarRoute } from '../../../constants/urls';

import {
  emitSetTourAvailabilityButtonEvent,
  emitTourAvailabilityImpressionEvent,
  EVENT_IDS,
} from '../../../analytics/events/payAndPublish';
import { useAuth } from '../../AuthProvider';
import SpinnerContainer from '../../SpinnerContainer';

type Props = {
  listingAlias: string;
  isOpen: boolean;
  onClose: () => void;
};

const PostActivationDashboardModal = ({ listingAlias, isOpen, onClose }: Props) => {
  const [displayTourWizard, setDisplayTourWizard] = useState(false);

  const queryClient = useQueryClient();
  const router = useRouter();
  const { user } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { data: touringTreatment, isLoading: isGetTouringTreatmentFetching } =
    useZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing(
      { listingAlias },
      { enabled: Boolean(listingAlias) },
    );

  const isFeed = listingDetails?.isFeed ?? false;
  const active = listingDetails?.active ?? false;
  const isMultifamily = listingDetails?.isMultifamily ?? false;
  const canBeRelisted = listingDetails?.canBeRelisted ?? false;
  const lastActivatedMs = listingDetails?.lastActivatedMs;

  const isInReactivationFlow = accessUtils.canAccessMLReactivationFlow({ lastActivatedMs, isFeed, isMultifamily });
  const canAccessRelisting = Boolean(canBeRelisted);
  const canAccessPostingPathTourScheduling = accessUtils.canAccessPostingPathTourScheduling({
    isFeed,
    isInReactivationFlow,
    canAccessRelisting,
    active,
  });

  const isFreemiumEnabled = listingDetails?.freemium?.isEnabled ?? false;
  const freemiumActiveProductType = listingDetails?.freemium?.activeProduct?.type;
  const freemiumActiveProductDuration = listingDetails?.freemium?.activeProduct?.duration;
  const isActiveProductTypePremium = isFreemiumEnabled && freemiumActiveProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;

  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });

  const shouldDisplayTourCalendar = canAccessPostingPathTourScheduling && touringTreatment?.isTouringEnabledForListing;
  const shouldDisplayTourWizard =
    !canAccessPostingPathTourScheduling &&
    !touringTreatment?.isTouringEnabledForListing &&
    !touringTreatment?.hasAvailability;
  const shouldDisplayTouring = shouldDisplayTourCalendar || shouldDisplayTourWizard;

  const networkSites = isRoomForRent ? 'Zillow and HotPads' : 'Zillow, HotPads, and Trulia';

  const csEventParams = {
    currentProperty: listingDetails,
    user: user ?? {},
  };

  useEffect(() => {
    if (shouldDisplayTouring && listingDetails && user) {
      emitTourAvailabilityImpressionEvent({
        currentProperty: listingDetails,
        user,
      });
    }
  }, [shouldDisplayTouring]);

  const handleGotItClick = () => {
    onClose();
  };

  const handleSetTourAvailabilityClick = () => {
    if (listingDetails && user) {
      emitSetTourAvailabilityButtonEvent({
        currentProperty: listingDetails,
        user,
      });
    }

    if (shouldDisplayTourWizard) {
      setDisplayTourWizard(true);
    } else {
      void router.push(getTourCalendarRoute({ listingAlias }));
      onClose();
    }
  };

  const onTourAvailabilityWizardClose = () => {
    setDisplayTourWizard(false);
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === ZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing_Path,
    });
    onClose();
  };

  const getButtonCTA = () => {
    let buttonText = 'Visit your overview';
    let onClick = handleGotItClick;

    if (shouldDisplayTouring) {
      buttonText = shouldDisplayTourWizard ? 'Set tour availability' : 'Add more availability';
      onClick = handleSetTourAvailabilityClick;
    } else if (listingDetails?.listingCriteria?.isEnabled) {
      buttonText = 'OK';
      onClick = handleGotItClick;
    }

    return (
      <Button
        size="lg"
        fluid={isScreenMobile}
        onClick={onClick}
        data-eventid={
          shouldDisplayTouring ? EVENT_IDS.POST_LISTING_DASHBOARD_MODAL_TOUR_AVAILABILITY_BUTTON : undefined
        }
        tone="brand"
        emphasis="filled"
      >
        {buttonText}
      </Button>
    );
  };

  if (isGetTouringTreatmentFetching) {
    return <SpinnerContainer />;
  }

  if (displayTourWizard) {
    return (
      <TourAvailabilityWizard
        onClose={onTourAvailabilityWizardClose}
        listingAlias={listingAlias}
        modalHeaderText={listingDetails?.street ?? ''}
        clickStreamMetadata={csEventParams}
        canAccessPostingPathTourDuration={true}
      />
    );
  }

  return (
    <Modal
      size={{ base: 'fullScreen', md: 'md', lg: 'lg' }}
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      dividers={false}
      body={
        <Box
          data-testid="PostActivationDashboardModal"
          data-eventid={
            shouldDisplayTouring ? EVENT_IDS.POST_LISTING_DASHBOARD_MODAL_TOUR_AVAILABILITY_IMPRESSION : undefined
          }
        >
          <Image src={RENT_HOME_PEOPLE_ILLUSTRATION_URL} alt="" role="presentation" width={300} height={189} />

          <Heading level={1} textStyle="heading-sm" css={{ marginTop: '600', marginBottom: '400' }}>
            Congratulations, you published your listing!
          </Heading>

          <Paragraph>
            Within the hour, your listing will post to {networkSites}
            —we’ll notify you once it’s live. It’s best practice to deactivate your listing when you’ve found a renter.
          </Paragraph>

          {isActiveProductTypePremium && !!freemiumActiveProductDuration && (
            <Paragraph css={{ marginTop: '600' }}>
              You can still access your premium benefits for {freemiumActiveProductDuration}, regardless of listing
              status.
            </Paragraph>
          )}

          {shouldDisplayTourWizard && (
            <Paragraph
              textStyle="body-bold"
              css={{ marginBottom: '400', marginTop: '600', color: 'text.accent.blue.strong' }}
            >
              Next, set your availability so you can book tours faster and more easily.
            </Paragraph>
          )}

          {shouldDisplayTourCalendar && (
            <Paragraph
              textStyle="body-bold"
              css={{ marginBottom: '400', marginTop: '600', color: 'text.accent.blue.strong' }}
            >
              Next, make sure interested tenants can find a time you&rsquo;re free for tours by adding more availability
              to your calendar.
            </Paragraph>
          )}
        </Box>
      }
      footer={<ButtonGroup aria-label="Post activation actions">{getButtonCTA()}</ButtonGroup>}
    />
  );
};

export default PostActivationDashboardModal;
